import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {assertAndContinue} from 'Asserts';
import {SampleStatusItem} from 'constants/SampleStatusConstants';
import {isSchemaTag, SchemaTag, Tag} from 'folder-items/FileConstants';
import {
  canProceedWithSamples,
  checkForSampleStatusWarnings,
  SampleStatusWarning,
} from 'inventory/sample-status/SampleStatusWarning';
import {SampleStatusWarningTrackingParams} from 'inventory/sample-status/SampleStatusWarningTracking';
import TagValueEditor from 'metadata/TagValueEditor';
import TagValueViewer from 'metadata/TagValueViewer';
import CurrentAndNewValueRow from 'samples/toolbar/CurrentAndNewValueRow';
import {renderPossibleExpirationDateConfirmation} from 'samples/valid-contents/ConfirmEntitiesExpirationDate';
import {getInitialTag, tagHasValue, tagsAreEqual} from 'schemas/tags/TagUtil';
import {FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import Modal from 'ui-library/modals/Modal';
import APIUtil from 'util/APIUtil';
import * as logging from 'util/Logging';

export interface TagEditorModalItem {
  id: string;
  schemaId?: string | null;
  tags: Array<Tag>;
}

export interface TagEditorModalProps {
  onRequestClose: () => void;
  onRequestSubmit: ({newTag, selectedStorableIds}: TagEditorModalResult) => void;
  tagName: string;
  selectedItems: Array<TagEditorModalItem>;
  sampleStatusWarningParams?: SampleStatusWarningTrackingParams;
}

export interface TagEditorModalResult {
  newTag: Tag;
  selectedStorableIds?: Array<string>;
}

interface TagEditorModalState {
  emptyItems: boolean;
  tag: Tag | undefined;
  canProceedOnSampleStatus: boolean;
  selectedSampleIds: Array<string>;
  shouldShowStatusWarnings: boolean;
  samples: Array<SampleStatusItem> | null;
  loading: boolean;
  expirationBanner: JSX.Element | null;
}

const MULTI_VALUE_TEXT = 'Multiple values';
const EMPTY_VALUE_TEXT = 'No value';

export default class TagEditorModal extends React.Component<TagEditorModalProps, TagEditorModalState> {
  constructor(props: TagEditorModalProps) {
    super(props);
    if (this.props.selectedItems.length === 0) {
      logging.warn('TagEditorModal was given an empty array of items - should have at least one');
      this.state = {
        emptyItems: true,
        tag: undefined,
        canProceedOnSampleStatus: false,
        selectedSampleIds: [],
        shouldShowStatusWarnings: false,
        samples: null,
        loading: false,
        expirationBanner: null,
      };
    } else {
      const currentTagInfo = this.getCurrentTag();

      this.state = {
        emptyItems: false,
        tag: currentTagInfo.isAutogenerated ? this.getDummyTag(currentTagInfo.tag) : currentTagInfo.tag,
        canProceedOnSampleStatus: false,
        selectedSampleIds: [],
        shouldShowStatusWarnings: false,
        samples: null,
        loading: this.props.sampleStatusWarningParams != null,
        expirationBanner: null,
      };
    }
  }

  override componentDidMount(): void {
    if (this.props.sampleStatusWarningParams != null) {
      this.checkForWarnings();
    }
    this.checkForExpirationConfirmation();
  }

  getDummyTag = (currentTag: Tag): Tag => {
    if (isSchemaTag(currentTag)) {
      // schema tag
      return getInitialTag(currentTag.tagSchemaField);
    } else {
      // custom tag
      return {name: currentTag.name};
    }
  };

  getCurrentTag(): {tag: Tag; isAutogenerated: boolean} {
    const targetTags = this.props.selectedItems
      .map((item) => item.tags.find((tag) => tag.name === this.props.tagName))
      .filter((tag): tag is Tag => tag != null);

    const allItemsHaveTag = this.props.selectedItems.length === targetTags.length;
    const representativeTag = targetTags[0];

    // We only want to show a value for the old tag and default the value for the new tag
    // if all items have the tag and have the same value for that tag
    // but the backend allows for bulk updating tags even if all items don't have the tag yet
    // so we allow that here, too
    let value;
    if (allItemsHaveTag && tagsAreEqual(targetTags)) {
      if (tagHasValue(representativeTag)) {
        return {tag: representativeTag, isAutogenerated: false};
      }
      value = EMPTY_VALUE_TEXT;
    } else {
      value = MULTI_VALUE_TEXT;
    }
    const blankTag = this.getDummyTag(representativeTag);
    blankTag.value = value;
    return {
      tag: blankTag,
      isAutogenerated: true,
    };
  }

  updateTagValue = (changedTagAttributes: Partial<Tag>): void => {
    // @ts-expect-error
    this.setState({tag: {...this.state.tag, ...changedTagAttributes}});
  };

  onRequestSubmit = (): void => {
    if (!this.state.tag) {
      assertAndContinue(false, 'Unexpected call to onRequestSubmit in the emptyItems case.');
      return;
    }
    this.props.onRequestSubmit({newTag: this.state.tag, selectedStorableIds: this.state.selectedSampleIds});
  };

  async checkForWarnings(): Promise<void> {
    const sampleIds = this.props.sampleStatusWarningParams?.sampleIds ?? [];
    if (sampleIds.length > 0 && APIUtil.isStorable(sampleIds[0])) {
      const {shouldShowWarning, samples} = await checkForSampleStatusWarnings({
        sampleIds,
      });
      this.setState({shouldShowStatusWarnings: shouldShowWarning, samples: samples ?? null});
      this.setSelectedSampleIds(sampleIds);
      this.setState({loading: false});
    } else {
      this.setState({shouldShowStatusWarnings: false, loading: false});
    }
  }

  async checkForExpirationConfirmation(): Promise<void> {
    const bioentityItems = this.props.selectedItems.filter(({id}) => APIUtil.isBiologicalEntity(id));
    // All items should be of the same schema; we filter out schema field menu options in TagEditorDropdown:getOptionDatas
    // if items of multiple schemas are selected.
    const tagSchemaId = bioentityItems.length > 0 ? bioentityItems[0].schemaId : null;
    const itemsAreOfSameSchema = bioentityItems.every(({schemaId}) => schemaId === tagSchemaId);
    if (tagSchemaId == null || !itemsAreOfSameSchema || bioentityItems.length === 0) {
      assertAndContinue(
        itemsAreOfSameSchema,
        'Expected TagEditorModal to be called with all items of the same schema. Schema field menu options should not appear otherwise.'
      );
      return;
    }
    const banner = await renderPossibleExpirationDateConfirmation(
      tagSchemaId,
      bioentityItems.map(({id}) => id),
      this.props.tagName
    );
    this.setState({expirationBanner: banner});
  }

  setCanProceedOnSampleStatus = (override: boolean): void => {
    this.setState({canProceedOnSampleStatus: override});
  };

  setSelectedSampleIds = (sampleIds: Array<string>): void => {
    this.setState({selectedSampleIds: sampleIds});
  };

  renderPossibleWarning = (): JSX.Element | null => {
    if (this.props.sampleStatusWarningParams == null) {
      return null;
    }
    const {sampleIds, ...statusWarningTrackingParams} = this.props.sampleStatusWarningParams;
    return this.state.shouldShowStatusWarnings && this.state.samples != null ? (
      <SampleStatusWarning
        sampleIds={sampleIds}
        selectedSampleIds={this.state.selectedSampleIds}
        setSelectedSampleIds={this.setSelectedSampleIds}
        shouldProceed={this.state.canProceedOnSampleStatus}
        setShouldProceed={this.setCanProceedOnSampleStatus}
        trackingParams={statusWarningTrackingParams}
        samples={this.state.samples}
      />
    ) : null;
  };

  renderEmpty = (): string => 'An error has occurred - no items provided for updating. Please try again.';

  renderEditor(): JSX.Element {
    return (
      <div className={css(this.state.shouldShowStatusWarnings && styles.formPadding)}>
        <CurrentAndNewValueRow
          label="value"
          currentValue={
            <span className={css(styles.current)}>
              <TagValueViewer tag={this.getCurrentTag().tag} showChipsAsLinks={false} />
            </span>
          }
          customNewValueColumnWidth={7}
          isNewValueRequired={false}
          newValueInput={
            <TagValueEditor
              onChange={this.updateTagValue}
              tag={this.state.tag! as SchemaTag}
              autoFocus={true}
              onKeyDown={noop}
            />
          }
        />
      </div>
    );
  }

  override render(): JSX.Element {
    return (
      <Modal
        buttons={[
          {
            text: 'Update',
            submit: true,
            disabled:
              this.state.loading ||
              !canProceedWithSamples(
                this.state.selectedSampleIds,
                this.state.shouldShowStatusWarnings,
                this.state.canProceedOnSampleStatus
              ),
          },
        ]}
        onRequestClose={this.props.onRequestClose}
        onRequestSubmit={this.onRequestSubmit}
        title={`Bulk update schema field: ${this.props.tagName}`}
        scrollable={false}
        expandHeight={false}
      >
        {this.state.emptyItems ? (
          this.renderEmpty()
        ) : this.state.loading ? (
          <CenteredHorizontalThrobber />
        ) : (
          <>
            {this.renderPossibleWarning()}
            {canProceedWithSamples(
              this.state.selectedSampleIds,
              this.state.shouldShowStatusWarnings,
              this.state.canProceedOnSampleStatus
            ) && this.renderEditor()}
            {this.state.expirationBanner}
          </>
        )}
      </Modal>
    );
  }
}

const styles = StyleSheet.create({
  formPadding: {paddingTop: '15px'},
  label: {
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
  current: {
    display: 'flex',
    flexDirection: 'column',
  },
});
